<template>
  <button
    v-if="show"
    type="button"
    :class="rootClass"
    :title="label"
    :aria-label="label"
    :aria-pressed="active"
    @click="toggle()"
  >
    <svg
      v-if="tone === 'icon'"
      class="fs-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <path
        v-if="!active"
        d="M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3"
      />
      <path
        v-else
        d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3"
      />
    </svg>
    <span v-else>{{ label }}</span>
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useDesktopFullscreen } from '../composables/useDesktopFullscreen'
import { useCompactLayout } from '../composables/useCompactLayout'
import { useI18n } from '../i18n/t'

const props = withDefaults(
  defineProps<{
    tone?: 'icon' | 'settings' | 'duel'
  }>(),
  { tone: 'icon' },
)

const { t } = useI18n()
const { menusCompact } = useCompactLayout()
const { active, available, toggle } = useDesktopFullscreen()

const show = computed(() => available.value && !menusCompact.value)

const label = computed(() =>
  active.value ? t('nav.fullscreenExit') : t('nav.fullscreen'),
)

const rootClass = computed(() => {
  if (props.tone === 'icon') {
    return ['icon-btn', { 'icon-active': active.value }]
  }
  if (props.tone === 'settings') {
    return ['scheme-btn', { active: active.value }]
  }
  return ['sfx-toggle']
})
</script>

<style scoped>
.fs-icon {
  width: 22px;
  height: 22px;
}

.scheme-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border: 1px solid var(--md-sys-color-outline-variant);
  border-radius: 20px;
  background: transparent;
  color: var(--md-sys-color-on-surface);
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.scheme-btn.active {
  background: var(--md-sys-color-primary);
  border-color: var(--md-sys-color-primary);
  color: var(--md-sys-color-on-primary);
}

.sfx-toggle {
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(232, 197, 71, 0.32);
  background: rgba(12, 14, 24, 0.9);
  color: #f4f0ea;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.03em;
  cursor: pointer;
}

.sfx-toggle:hover {
  border-color: #e8c547;
  color: #fde68a;
}
</style>
